import { Avatar, Box, Button, Chip, CircularProgress, Collapse, IconButton, ListItem, Paper, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material"
import { alpha } from "@mui/material/styles";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Fragment, memo, useCallback, useEffect, useRef, useState } from "react";
import Markdown from "markdown-to-jsx";
import { format } from "date-fns";
import lottie from "lottie-web";
import { parseTextWithButtons, type BrodiButtonItem, type BrodiCompareItem, type BrodiMateriItem, type BrodiPriceData, type BrodiCtaData, type BrodiPointItem, type BrodiSkeletonKind } from "src/utils/brodiButtonParser";

/** Status tool call di badge chat */
export type ToolCallBadge = {
  tool_call_id: string;
  tool_name: string;
  status: "running" | "done" | "error";
  success?: boolean;
};

/** Label ramah untuk tool_name backend (kandidat utama = label ke-2) */
export const formatToolLabel = (toolName: string): string => {
  const map: Record<string, string> = {
    get_my_profile: "Melihat Profil Kamu",
    get_my_membership: "Melihat Membership Kamu",
    get_my_ecourse: "Melihat E-Course Kamu",
    get_my_materi: "Mencari Materi Kamu",
    search_course_global: "Mencari Course Global",
    search_materi_global: "Mencari Materi Global",
    cari_daftar_program_brofesional: "Mencari Daftar Program",
    detail_program_brofesional: "Melihat Detail Program",
    bandingkan_paket_brofesional: "Membandingkan Paket",
    highlight_harga_brofesional: "Menyorot Harga Paket",
    cta_program_brofesional: "Menampilkan Tombol Daftar",
  };
  if (map[toolName]) return map[toolName];
  return (toolName || "tool")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Baris chip tool call di dalam bubble (SSE ToolCallStarted/Completed) */
const ToolCallBadges = memo(({ tools }: { tools?: ToolCallBadge[] }) => {
  if (!tools?.length) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.75,
        mb: 1,
        maxWidth: "100%",
      }}
    >
      {tools.map((tool) => {
        const isRunning = tool.status === "running";
        const isError = tool.status === "error" || tool.success === false;
        const label = formatToolLabel(tool.tool_name);

        return (
          <Chip
            key={tool.tool_call_id}
            size="small"
            variant="outlined"
            icon={
              isRunning ? (
                <CircularProgress size={12} thickness={5} color="inherit" sx={{ ml: "6px !important" }} />
              ) : isError ? (
                <CloseIcon sx={{ fontSize: "14px !important" }} />
              ) : (
                <CheckIcon sx={{ fontSize: "14px !important" }} />
              )
            }
            label={label}
            sx={{
              height: 26,
              fontSize: "0.75rem",
              fontWeight: 500,
              maxWidth: "100%",
              cursor: "default",
              borderColor: isError
                ? "error.light"
                : isRunning
                  ? "primary.light"
                  : "success.light",
              bgcolor: (theme) =>
                isError
                  ? alpha(theme.palette.error.main, 0.08)
                  : isRunning
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.success.main, 0.08),
              color: isError ? "error.dark" : isRunning ? "primary.dark" : "success.dark",
              "& .MuiChip-icon": {
                color: "inherit",
              },
              "& .MuiChip-label": {
                px: 0.75,
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              ...(isRunning
                ? {
                    animation: "brodiToolPulse 1.6s ease-in-out infinite",
                    "@keyframes brodiToolPulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.72 },
                    },
                    "@media (prefers-reduced-motion: reduce)": {
                      animation: "none",
                    },
                  }
                : {}),
            }}
          />
        );
      })}
    </Box>
  );
});

// Formatter Rupiah untuk harga paket (IDR, tanpa desimal)
const formatRupiah = (value: number | undefined): string => {
  if (typeof value !== "number" || !isFinite(value)) return "";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(value);
};

const CodeBlock = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      component="pre"
      sx={{
        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
        borderRadius: '4px',
        p: 1.5,
        my: 1,
        fontSize: '0.9rem',
        overflowX: 'auto',
      }}
    >
      {children}
    </Box>
  );
};

const markdownOptions = {
  overrides: {
    pre: {
      component: CodeBlock,
    },
  },
};

/**
 * Pisahkan konten <think>...</think> (juga incomplete saat stream) dari teks utama.
 * Tag think tidak ikut ke Markdown balasan; ditampilkan di section collapsible.
 */
export function extractThinkBlocks(text: string): { mainText: string; thoughts: string[] } {
  if (!text) return { mainText: "", thoughts: [] };

  const thoughts: string[] = [];
  // Closed blocks: <think>...</think> (case-insensitive, multiline)
  const closedRe = /<\s*think\s*>([\s\S]*?)<\s*\/\s*think\s*>/gi;
  let mainText = text.replace(closedRe, (_full, inner: string) => {
    const trimmed = (inner ?? "").trim();
    if (trimmed) thoughts.push(trimmed);
    return "";
  });

  // Streaming: open tag tanpa penutup di ujung string
  const openRe = /<\s*think\s*>([\s\S]*)$/i;
  const openMatch = mainText.match(openRe);
  if (openMatch) {
    const partial = (openMatch[1] ?? "").trim();
    if (partial) thoughts.push(partial);
    mainText = mainText.slice(0, openMatch.index ?? 0);
  }

  // Rapikan sisa whitespace berlebih antar blok
  mainText = mainText.replace(/\n{3,}/g, "\n\n").trim();
  return { mainText, thoughts };
}

/** Section collapsible: hasil thinking di dalam tag <think> */
const ThinkCollapsePanel = memo(({ thoughts }: { thoughts: string[] }) => {
  const [open, setOpen] = useState(false);
  const body = thoughts.filter(Boolean).join("\n\n").trim();
  if (!body) return null;

  return (
    <Box
      sx={{
        mb: 1.25,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.03),
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Tutup hasil thinking" : "Buka hasil thinking"}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          width: "100%",
          border: "none",
          m: 0,
          px: 1.25,
          py: 0.85,
          bgcolor: "transparent",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "inherit",
          transition: "background-color 0.2s ease",
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.action.hover, 0.6),
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: -2,
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "text.secondary",
            lineHeight: 1.3,
          }}
        >
          Hasil Thinking Brodi
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            color: "text.secondary",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            px: 1.25,
            pb: 1.25,
            pt: 0.25,
            borderTop: "1px solid",
            borderColor: "divider",
            color: "text.secondary",
            fontSize: "0.8125rem",
            lineHeight: 1.45,
            "& p": { m: 0, "& + p": { mt: 0.75 } },
            "& ul, & ol": { m: 0, pl: 2 },
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          <Markdown options={markdownOptions}>{body}</Markdown>
        </Box>
      </Collapse>
    </Box>
  );
});

const botBubbleSx = {
  p: 1.5,
  maxWidth: "88%",
  minWidth: 0,
  bgcolor: "action.hover",
  color: "text.primary",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "16px 16px 16px 4px",
  boxShadow: "none",
  transition: "all 0.2s ease-out",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontSize: "0.9375rem",
  lineHeight: 1.5,
  "& p": { m: 0, "& + p": { mt: 1 } },
  "& ul, & ol": { m: 0, pl: 2.5 },
} as const;

const SimpleDotsLoader = () => {
  const [activeDot, setActiveDot] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveDot(prev => (prev + 1) % 3);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', minHeight: 16 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: i === activeDot ? 'primary.main' : 'action.disabled',
            transition: 'background-color 0.25s ease',
          }}
        />
      ))}
    </Box>
  );
};

/** Loading state sebelum token SSE pertama: Ghostsmart lottie + "Brodi is Thinking..." */
const BrodiThinkingLoader = memo(() => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Kosongkan dulu jika remount cepat (hindari SVG double-render)
    el.innerHTML = "";

    const anim = lottie.loadAnimation({
      container: el,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: "/lottie/ghostsmart.json",
    });

    return () => {
      anim.destroy();
      el.innerHTML = "";
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        minWidth: 140,
        py: 0.5,
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: 132,
          height: 132,
          lineHeight: 0,
          "& svg": {
            width: "100% !important",
            height: "100% !important",
            display: "block",
          },
        }}
      />
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 500,
          color: "text.secondary",
          lineHeight: 1.3,
          textAlign: "center",
        }}
      >
        Brodi is Thinking...
      </Typography>
    </Box>
  );
});

/** Skeleton placeholder: ditampilkan saat SSE menampilkan (((BRODI_ yang belum lengkap */
const BrodiMarkerSkeleton = memo(({ kind }: { kind: BrodiSkeletonKind }) => {
  if (kind === "button") {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1, mb: 0.5 }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" width={88 + i * 12} height={32} sx={{ borderRadius: 999 }} />
        ))}
      </Box>
    );
  }

  if (kind === "materi") {
    return (
      <Box sx={{ display: "flex", gap: 1, mt: 1.5, mb: 0.5, overflow: "hidden" }}>
        {[0, 1].map((i) => (
          <Box
            key={i}
            sx={{
              width: 140,
              flexShrink: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              overflow: "hidden",
            }}
          >
            <Skeleton variant="rectangular" width="100%" height={140} />
            <Box sx={{ p: 1 }}>
              <Skeleton width="90%" height={14} />
              <Skeleton width="55%" height={14} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (kind === "compare") {
    return (
      <Box sx={{ mt: 1.5, mb: 1.25, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Skeleton variant="rectangular" width={36} height={36} />
          <Skeleton width="45%" height={16} />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5 }}>
          <Skeleton variant="rectangular" height={56} />
          <Skeleton variant="rectangular" height={56} />
        </Box>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} height={36} sx={{ mt: 0.75 }} />
        ))}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75, mt: 1 }}>
          <Skeleton variant="rectangular" height={32} sx={{ borderRadius: 999 }} />
          <Skeleton variant="rectangular" height={32} sx={{ borderRadius: 999 }} />
        </Box>
      </Box>
    );
  }

  if (kind === "price") {
    return (
      <Box
        sx={{
          mt: 1.5,
          mb: 1.5,
          p: 1.75,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "14px",
          width: "100%",
        }}
      >
        <Skeleton width={72} height={12} sx={{ mb: 0.75 }} />
        <Skeleton width="70%" height={20} />
        <Skeleton width="45%" height={14} sx={{ mt: 0.5 }} />
        <Skeleton width="40%" height={22} sx={{ mt: 1.25 }} />
        <Box sx={{ display: "flex", gap: 0.75, mt: 1 }}>
          <Skeleton variant="rectangular" width={64} height={22} />
          <Skeleton variant="rectangular" width={56} height={22} />
        </Box>
        <Skeleton height={14} sx={{ mt: 1 }} />
        <Skeleton height={14} width="85%" sx={{ mt: 0.5 }} />
        <Skeleton variant="rectangular" height={36} sx={{ mt: 1.5, borderRadius: 999 }} />
      </Box>
    );
  }

  if (kind === "cta") {
    return (
      <Box
        sx={{
          mt: 1.5,
          mb: 1.5,
          p: 1.75,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "14px",
          width: "100%",
        }}
      >
        <Skeleton width="75%" height={18} />
        <Skeleton width="95%" height={14} sx={{ mt: 0.75 }} />
        <Skeleton width="60%" height={14} sx={{ mt: 0.5 }} />
        <Skeleton variant="rectangular" height={36} sx={{ mt: 1.5, borderRadius: 999 }} />
      </Box>
    );
  }

  // unknown: blok generik
  return (
    <Box sx={{ mt: 1.25, mb: 0.75, width: "100%" }}>
      <Skeleton variant="rectangular" height={88} sx={{ borderRadius: 2 }} />
      <Skeleton width="50%" height={12} sx={{ mt: 0.75 }} />
    </Box>
  );
});

/** Tombol retry saat helper BRODI_* gagal diparse */
const BrodiParseFailedRetry = memo(({
  disabled = false,
  onRetry,
}: {
  disabled?: boolean;
  onRetry?: () => void;
}) => {
  return (
    <Box sx={{ mt: 1.25, mb: 0.75, width: "100%", minWidth: 0 }}>
      <Button
        size="small"
        variant="outlined"
        color="warning"
        disabled={disabled}
        onClick={() => {
          if (!disabled) onRetry?.();
        }}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8125rem",
          borderRadius: 2,
          py: 0.75,
          px: 1.5,
          lineHeight: 1.35,
          justifyContent: "flex-start",
          textAlign: "left",
          whiteSpace: "normal",
          maxWidth: "100%",
          borderColor: "warning.light",
          color: "warning.dark",
          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
          "&:hover": {
            borderColor: "warning.main",
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
          },
        }}
      >
        Brodi Gagal menampilkan, coba lagi?
      </Button>
    </Box>
  );
});

const TypingBubble = memo(({ text, tools, showThinking, onButtonClick, onCompareClick, onPriceClick, onCtaClick, onHelperRetry }: { text?: string | null; tools?: ToolCallBadge[]; showThinking?: boolean; onButtonClick?: (title: string) => void; onCompareClick?: (item: BrodiCompareItem, programId?: number, programUrl?: string) => void; onPriceClick?: (data: BrodiPriceData) => void; onCtaClick?: (data: BrodiCtaData) => void; onHelperRetry?: () => void }) => {
  const hasText = Boolean(text?.trim());
  const hasTools = Boolean(tools?.length);

  // Guard: jangan render bubble kosong (tanpa tools maupun teks)
  if (!hasText && !hasTools) return null;

  const { mainText, thoughts } = extractThinkBlocks(text ?? "");
  // streaming=true: (((BRODI_ incomplete → skeleton (bukan nunggu selesai helper)
  const segments = mainText.trim() ? parseTextWithButtons(mainText, { streaming: true }) : [];
  const hasRichCards = segments.some((s) => s.type !== "text") || thoughts.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        ...botBubbleSx,
        maxWidth: hasRichCards || hasTools ? "100%" : "88%",
      }}
    >
      <ToolCallBadges tools={tools} />
      <ThinkCollapsePanel thoughts={thoughts} />

      {showThinking && !mainText.trim() && thoughts.length === 0 && (
        <Box sx={{ py: 0.5 }}>
          <BrodiThinkingLoader />
        </Box>
      )}

      {showThinking && !mainText.trim() && thoughts.length > 0 && (
        <Box sx={{ py: 0.25 }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.75rem" }}>
            masih berpikir...
          </Typography>
        </Box>
      )}

      {segments.map((segment, idx) => (
        <Fragment key={idx}>
          {segment.type === "text" && (
            <Markdown options={markdownOptions}>{segment.content}</Markdown>
          )}
          {segment.type === "skeleton" && (
            <BrodiMarkerSkeleton kind={segment.kind} />
          )}
          {segment.type === "failed" && (
            <BrodiParseFailedRetry disabled={true} />
          )}
          {segment.type === "button" && (
            <BrodiButtonGroup
              items={segment.data.items}
              onButtonClick={onButtonClick}
              disabled={true} // Always disabled during streaming
              keyPrefix={`typing-${idx}`}
            />
          )}
          {segment.type === "materi" && (
            <BrodiMateriCard
              items={segment.data.items ?? []}
              disabled={true} // Always disabled during streaming
              keyPrefix={`typing-materi-${idx}`}
            />
          )}
          {segment.type === "compare" && (
            <BrodiCompareCard
              items={segment.data.items ?? []}
              programId={segment.data.program_id}
              programTitle={segment.data.program_title}
              programImage={segment.data.program_image}
              programUrl={segment.data.url}
              program={segment.data.program}
              onCompareClick={onCompareClick}
              disabled={true} // Always disabled during streaming
              keyPrefix={`typing-compare-${idx}`}
            />
          )}
          {segment.type === "price" && (
            <BrodiPriceCard
              data={segment.data}
              onPriceClick={onPriceClick}
              disabled={true} // Always disabled during streaming
              keyPrefix={`typing-price-${idx}`}
            />
          )}
          {segment.type === "cta" && (
            <BrodiCtaCard
              data={segment.data}
              onCtaClick={onCtaClick}
              disabled={true} // Always disabled during streaming
            />
          )}
        </Fragment>
      ))}

      {hasText && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "right",
            color: "text.disabled",
            mt: 0.75,
            fontSize: "0.6875rem",
          }}
        >
          {mainText.trim() ? "mengetik..." : thoughts.length > 0 ? "thinking..." : "mengetik..."}
        </Typography>
      )}
    </Paper>
  );
});

// BrodiButtonGroup component for rendering button options from BRODI_BUTTON marker
const BrodiButtonGroup = memo(({
  items,
  onButtonClick,
  disabled = false,
  keyPrefix = "btn"
}: {
  items: BrodiButtonItem[];
  onButtonClick?: (title: string) => void;
  disabled?: boolean;
  keyPrefix?: string;
}) => {
  if (!items?.length) return null;

  return (
    // Container flex wrap: tiap button jadi pill terpisah, turun ke baris baru jika penuh
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, mb: 0.5 }}>
      {items.map((item, index) => (
        <Button
          key={`${keyPrefix}-${index}`}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => onButtonClick?.(item?.title ?? "")}
          disabled={disabled || !item?.title}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8125rem',
            lineHeight: 1.35,
            py: 0.5,
            px: 1.5,
            minHeight: 32,
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {item?.title}
        </Button>
      ))}
    </Box>
  );
});

// BrodiMateriCard component for rendering materi/e-course cards from BRODI_MATERI marker
const BrodiMateriCard = memo(({
  items,
  onMateriClick,
  disabled = false,
  keyPrefix = "materi"
}: {
  items: BrodiMateriItem[];
  onMateriClick?: (item: BrodiMateriItem) => void;
  disabled?: boolean;
  keyPrefix?: string;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  // Update state tombol prev/next berdasarkan posisi scroll saat ini
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScroll({
      left: el.scrollLeft > 0,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  // Ref stabil: ukur posisi scroll awal saat container pertama kali di-mount
  const scrollContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      scrollRef.current = el;
      if (el) updateScrollState();
    },
    [updateScrollState]
  );

  // Geser carousel sejauh satu kartu (±148px: width 140 + gap)
  const scrollByAmount = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  };

  if (!items?.length) return null;

  // Responsive card width: 160px mobile, 140px desktop
  const CARD_WIDTH = isMobile ? 160 : 140;
  const SCROLL_STEP = CARD_WIDTH + 8;

  return (
    // Container carousel: kartu materi discroll horizontal (snap per kartu), tombol prev/next di sisi
    <Box sx={{ position: 'relative', mt: 1.5, mb: 0.5, width: '100%' }}>
      {/* Tombol prev: disabled saat sudah di awal / mode streaming */}
      <IconButton
        size="small"
        aria-label="Materi sebelumnya"
        disabled={disabled || !canScroll.left}
        onClick={() => scrollByAmount(-SCROLL_STEP)}
        sx={{
          position: 'absolute',
          left: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
          '&:hover': { bgcolor: 'background.paper' },
        }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      {/* Scroll container: horizontal, snap per kartu, scrollbar disembunyikan total */}
      <Box
        ref={scrollContainerRef}
        onScroll={updateScrollState}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          gap: 1,
          py: 0.5,
          // Mobile: padding horizontal biar card pertama/terakhir gak nempel tepi
          px: isMobile ? 1 : 0,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
        }}
      >
        {items.map((item, index) => (
          <Box
            key={`${keyPrefix}-${index}`}
            role={disabled ? undefined : "button"}
            tabIndex={disabled ? -1 : 0}
            onClick={() => {
              if (!disabled) onMateriClick?.(item);
            }}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onMateriClick?.(item);
              }
            }}
            sx={{
              flexShrink: 0,
              width: CARD_WIDTH,
              scrollSnapAlign: 'start',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              overflow: 'hidden',
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              transition: 'all 0.2s ease-out',
              '&:hover': disabled ? {} : { borderColor: 'primary.main', boxShadow: 1 },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
            }}
          >
            {/* Foto persegi 1:1 — objectFit contain agar tidak terpotong */}
            {item?.foto_course ? (
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={item.foto_course}
                  alt={item?.nama_materi ?? "Materi"}
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    // Fallback ke default bila gambar error/404 — cegah loop kalau default ikut error
                    if (!target.src.endsWith("/android-chrome-512x512.png")) {
                      target.src = "/android-chrome-512x512.png";
                    }
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                  }}
                />
              </Box>
            ) : (
              // Placeholder kalau foto_course tidak ada — jangan crash
              <Box sx={{
                width: '100%',
                aspectRatio: '1 / 1',
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Materi
                </Typography>
              </Box>
            )}
            {/* Container teks di bawah gambar */}
            <Box sx={{ minWidth: 0, flex: 1, p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.35,
                  fontSize: '0.8125rem',
                  color: '#101828',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {item?.nama_materi || "Materi"}
              </Typography>
              {item?.nama_membership && (
                <Chip
                  size="small"
                  label={item.nama_membership}
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.75rem',
                    alignSelf: 'flex-start',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      px: 0.75,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Tombol next: disabled saat sudah di akhir / mode streaming */}
      <IconButton
        size="small"
        aria-label="Materi berikutnya"
        disabled={disabled || !canScroll.right}
        onClick={() => scrollByAmount(SCROLL_STEP)}
        sx={{
          position: 'absolute',
          right: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
          '&:hover': { bgcolor: 'background.paper' },
        }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

// BrodiCompareCard component for rendering program comparison from BRODI_COMPARE marker
// Tabel perbandingan: kolom label sticky kiri (tidak ikut scroll), nilai paket sejajar di kanan.
// Panel sempit (3 paket) → scroll horizontal; panel lebar → semua kolom muat tanpa scroll.
// items.length === 1 → satu kolom; === 2 → dua kolom; >= 3 → tiga kolom per baris, sisanya turun.
const BrodiCompareCard = memo(({
  items,
  programId,
  programTitle,
  programImage,
  programUrl,
  program,
  onCompareClick,
  disabled = false,
  keyPrefix = "compare"
}: {
  items: BrodiCompareItem[];
  // Flat fields (old structure)
  programId?: number;
  programTitle?: string;
  programImage?: string;
  programUrl?: string;
  // Nested program object (new structure) - passed via program data from parser
  program?: {
    id?: number;
    title?: string;
    image?: string;
  };
  onCompareClick?: (item: BrodiCompareItem, programId?: number, programUrl?: string) => void;
  disabled?: boolean;
  keyPrefix?: string;
}) => {
  if (!items?.length) return null;

  // Kelompokkan items menjadi maksimal 3 kolom; chunk terakhir bisa berisi 1-2 item
  const chunks: BrodiCompareItem[][] = [];
  for (let i = 0; i < items.length; i += 3) {
    chunks.push(items.slice(i, i + 3));
  }

  // Nilai per kolom; kalau tidak ada → dash "−"
  const dash = (value: string | null | undefined): string => (value ? value : "−");

  // Nilai teks fitur (dash kalau kosong)
  const renderTextValue = (value: string | null | undefined) => (
    <Typography
      sx={{ wordBreak: 'break-word', lineHeight: 1.35, fontSize: '0.8125rem', color: 'text.primary' }}
    >
      {dash(value)}
    </Typography>
  );

  // Nilai ikon fitur: ✓ hijau kalau true, X abu/error kalau false/undefined
  const renderIconValue = (flag: boolean | undefined) =>
    flag ? (
      <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
    ) : (
      <CloseIcon sx={{ fontSize: 16, color: 'error.main' }} />
    );

  // Normalisasi point: support string[] lama dan object[] baru ({name, status})
  const pointName = (p: string | BrodiPointItem): string => (typeof p === "string" ? p : p?.name ?? "");
  const pointStatus = (p: string | BrodiPointItem): boolean => (typeof p === "string" ? true : p?.status === true);

  // Cek apakah paket punya point dengan nama tertentu (object baru: hanya dihitung kalau status true)
  const hasPoint = (it: BrodiCompareItem | undefined, name: string): boolean => {
    if (!it || !Array.isArray(it.points)) return false;
    return it.points.some((p) => pointName(p) === name && pointStatus(p));
  };

  // Nilai teks per kolom (1-3 kolom sesuai jumlah item dalam chunk)
  const rowValues = (values: (string | null | undefined)[]): React.ReactNode[] =>
    values.map((v) => renderTextValue(v));

  // Nilai ikon per kolom (1-3 kolom)
  const rowIcons = (flags: (boolean | undefined)[]): React.ReactNode[] =>
    flags.map((f) => renderIconValue(f));

  // Label fitur: kecil, uppercase, wrap natural (TANPA line-clamp — teks harus selalu kebaca penuh)
  const renderFeatureLabel = (label: string, opts: { italic?: boolean; uppercase?: boolean } = {}) => (
    <Typography
      sx={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        fontWeight: 600,
        fontSize: '0.75rem',
        color: 'text.secondary',
        textTransform: opts.uppercase === false ? 'none' : 'uppercase',
        fontStyle: opts.italic ? 'italic' : 'normal',
        lineHeight: 1.35,
        px: 0.5,
        pb: 0.25,
      }}
    >
      {label}
    </Typography>
  );

  // Styling cell label sticky (kolom pertama). PENTING: pakai <td>/<th> — sticky pada table cell
  // bekerja penuh, beda dengan grid item yang terkonstrain oleh grid area sendiri (label "lepas"
  // ikut ke-scroll begitu scroll melebihi lebar kolom → fitur ilang di layar kecil).
  const stickyLabelCellSx = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    bgcolor: 'action.hover',
    borderRight: '1px solid',
    borderColor: 'divider',
    minWidth: 104,
    maxWidth: 120,
    textAlign: 'left',
    verticalAlign: 'middle',
    p: '6px 8px',
  } as const;

  // Styling cell nilai paket (kolom 2+)
  const valueCellSx = {
    minWidth: 140,
    p: '6px 8px',
    textAlign: 'center',
    verticalAlign: 'middle',
  } as const;

  // Baris fitur: label sticky kiri (tidak ikut scroll horizontal), nilai paket sejajar di kanan.
  const renderFeatureRow = (
    label: string,
    values: React.ReactNode[],
    isLast: boolean,
    labelOpts: { italic?: boolean; uppercase?: boolean } = {}
  ) => (
    <Box component="tr" sx={{ borderBottom: isLast ? 'none' : '1px solid', borderColor: 'divider' }}>
      <Box component="td" sx={stickyLabelCellSx}>
        {renderFeatureLabel(label, labelOpts)}
      </Box>
      {values.map((value, valueIndex) => (
        <Box key={`${keyPrefix}-value-${valueIndex}`} component="td" sx={valueCellSx}>
          {value}
        </Box>
      ))}
    </Box>
  );

  // Header kolom paket (title + harga), clickable membuka halaman program
  const renderHeaderCell = (item: BrodiCompareItem | undefined, guaranteed: boolean) => (
    <Box
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? -1 : 0}
      aria-label={`Buka ${item?.title ?? "paket"}`}
      onClick={() => {
        if (!disabled) onCompareClick?.(item, programId, programUrl);
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCompareClick?.(item, programId, programUrl);
        }
      }}
      sx={{
        p: 1,
        minWidth: 0,
        bgcolor: guaranteed
          ? (theme) => alpha(theme.palette.primary.main, 0.08)
          : 'grey.50',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease-out',
        '&:hover': disabled
          ? {}
          : { bgcolor: guaranteed ? (theme) => alpha(theme.palette.primary.main, 0.14) : 'action.hover' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.875rem',
          lineHeight: 1.35,
          wordBreak: 'break-word',
          color: '#101828',
        }}
      >
        {item?.title || "Paket"}
      </Typography>
      {/* Harga: coret kalau ada gimmick_price, harga utama kalau price > 0 */}
      {(item?.price ?? 0) > 0 || (item?.gimmick_price ?? 0) > 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
          {(item?.gimmick_price ?? 0) > 0 && (
            <Typography sx={{ textDecoration: 'line-through', color: 'grey.500', fontSize: '0.75rem' }}>
              {formatRupiah(item?.gimmick_price)}
            </Typography>
          )}
          {(item?.price ?? 0) > 0 && (
            <Typography sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.875rem', lineHeight: 1.35 }}>
              {formatRupiah(item?.price)}
            </Typography>
          )}
        </Box>
      ) : null}
    </Box>
  );

  return (
    // Container scroll horizontal: di panel sempit (3 paket) muncul scrollbar, kolom label sticky tetap terlihat
    <Box sx={{ mt: 1.25, mb: 1.25, width: '100%', maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
      {/* Header program: image + title, tampil hanya kalau program_title ada (support both old & new structure) */}
      {programTitle || program?.title ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          {programImage || program?.image ? (
            <Box
              component="img"
              src={programImage || program?.image}
              alt={programTitle || program?.title || "Program"}
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement;
                // Fallback ke default bila gambar error/404 — cegah loop kalau default ikut error
                if (!target.src.endsWith("/android-chrome-512x512.png")) {
                  target.src = "/android-chrome-512x512.png";
                }
              }}
              sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            // Placeholder saat program_image kosong
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                Program
              </Typography>
            </Box>
          )}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.875rem',
              lineHeight: 1.35,
              color: '#101828',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {programTitle || program?.title}
          </Typography>
        </Box>
      ) : null}

      {chunks.map((chunk, chunkIndex) => {
        // cols dihitung per chunk tapi grid pakai auto-fit responsive
        const chunkCols = chunk.length;

        // Blok fitur hanya dirender kalau field-nya ada minimal di satu paket dalam chunk
        const hasDuration = chunk.some((it) => typeof it?.duration_days === "number" && (it?.duration_days ?? 0) > 0);
        const hasSession = chunk.some((it) => typeof it?.session_quota === "number" && (it?.session_quota ?? 0) > 0);
        const hasMode = chunk.some((it) => typeof it?.is_online === "boolean");
        const hasGuarantee = chunk.some((it) => typeof it?.is_guaranteed === "boolean");

        // Union nama-nama point dari semua paket dalam chunk (dedupe, urut kemunculan; support string[] lama & object[] baru)
        const unionPoints = Array.from(
          new Set(
            chunk
              .flatMap((it) => (Array.isArray(it?.points) ? it?.points ?? [] : []))
              .map((p) => pointName(p))
          )
        ).filter((n) => n.length > 0);
        const pointBlockCount =
          unionPoints.length > 0 ? Math.min(unionPoints.length, 3) + (unionPoints.length > 3 ? 1 : 0) : 0;
        const totalBlocks =
          (hasDuration ? 1 : 0) +
          (hasSession ? 1 : 0) +
          (hasMode ? 1 : 0) +
          (hasGuarantee ? 1 : 0) +
          pointBlockCount;

        // Penanda blok terakhir chunk (tanpa borderBottom biar rapi)
        let blockIndex = 0;
        const isBlockLast = () => {
          blockIndex += 1;
          return blockIndex === totalBlocks;
        };

        return (
          <Box
            key={`${keyPrefix}-chunk-${chunkIndex}`}
            sx={chunkIndex > 0 ? { borderTop: '1px solid', borderColor: 'divider', mt: 1.5 } : undefined}
          >
            {/* Tabel chunk: thead + tbody dalam satu <table> supaya sticky kolom label bekerja penuh */}
            <Box
              component="table"
              sx={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}
            >
            {/* Header kolom paket: th pertama placeholder sticky kiri + th paket clickable */}
            <Box component="thead">
              <Box component="tr">
                <Box
                  component="th"
                  sx={{
                    ...stickyLabelCellSx,
                    zIndex: 3,
                    borderBottom: totalBlocks > 0 ? '1px solid' : 'none',
                  }}
                />
                {chunk.map((it, idx) => (
                  <Box
                    key={`${keyPrefix}-chunk-${chunkIndex}-header-${idx}`}
                    component="th"
                    sx={{
                      p: 0,
                      minWidth: 140,
                      borderBottom: totalBlocks > 0 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                    }}
                  >
                    {renderHeaderCell(it, !!it?.is_guaranteed)}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Blok fitur: durasi, sesi, mode, garansi, fasilitas + baris tombol */}
            <Box component="tbody">
              {hasDuration &&
                renderFeatureRow(
                  "Durasi",
                  rowValues(chunk.map((it) => (it?.duration_days != null && it?.duration_days > 0 ? `${it?.duration_days} hari` : null))),
                  isBlockLast()
                )}
              {hasSession &&
                renderFeatureRow(
                  "Sesi",
                  rowValues(chunk.map((it) => (it?.session_quota != null && it?.session_quota > 0 ? `${it?.session_quota} sesi` : null))),
                  isBlockLast()
                )}
              {hasMode &&
                renderFeatureRow(
                  "Mode",
                  rowValues(chunk.map((it) => (typeof it?.is_online === "boolean" ? (it?.is_online ? "Online" : "Offline") : null))),
                  isBlockLast()
                )}
              {hasGuarantee &&
                renderFeatureRow(
                  "Garansi Lolos",
                  rowIcons(chunk.map((it) => it?.is_guaranteed)),
                  isBlockLast()
                )}

              {/* Fasilitas utama: union points, max 3 blok, sisanya "+N lainnya" */}
              {unionPoints.length > 0 &&
                unionPoints.slice(0, 3).map((point, pointIndex) => (
                  <Fragment key={`${keyPrefix}-chunk-${chunkIndex}-point-${pointIndex}`}>
                    {renderFeatureRow(
                      point,
                      rowIcons(chunk.map((it) => hasPoint(it, point))),
                      isBlockLast(),
                      {}
                    )}
                  </Fragment>
                ))}
              {unionPoints.length > 3 &&
                renderFeatureRow(
                  `+${unionPoints.length - 3} lainnya`,
                  chunk.map(() => null),
                  isBlockLast(),
                  { italic: true, uppercase: false }
                )}

              {/* Baris tombol "Lihat Paket": td placeholder sticky kiri + td tombol per paket */}
              <Box component="tr">
                <Box component="td" sx={stickyLabelCellSx} />
                {chunk.map((it, idx) => (
                  <Box key={`${keyPrefix}-chunk-${chunkIndex}-button-${idx}`} component="td" sx={valueCellSx}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      disabled={disabled}
                      onClick={() => {
                        if (!disabled) onCompareClick?.(it, programId, programUrl);
                      }}
                      sx={{
                        width: '100%',
                        borderRadius: 999,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                        py: 0.5,
                        minHeight: 32,
                      }}
                    >
                      Lihat Paket
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
});

// BrodiPriceCard component for rendering a single recommended package from BRODI_PRICE marker
// SATU pricing card (bukan carousel/tabel): label "Rekomendasi", nama paket, harga fokus, chips durasi/mode,
// highlights (max 3), dan tombol "Lihat Detail Paket" yang membuka halaman program.
const BrodiPriceCard = memo(({
  data,
  onPriceClick,
  disabled = false,
  keyPrefix = "price"
}: {
  data: BrodiPriceData;
  onPriceClick?: (data: BrodiPriceData) => void;
  disabled?: boolean;
  keyPrefix?: string;
}) => {
  // Guard: tanpa data object → jangan render apapun
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;

  // Harga coret hanya muncul kalau gimmick_price > 0 DAN lebih besar dari harga utama
  const hasGimmick = (data?.gimmick_price ?? 0) > 0 && (data?.gimmick_price ?? 0) > (data?.price ?? 0);
  const hasPrice = typeof data?.price === "number" && data?.price > 0;
  const hasDuration = typeof data?.duration_days === "number" && (data?.duration_days ?? 0) > 0;

  // Highlights maksimal 3 item (display only — data asli tidak diubah)
  // Normalisasi: support string[] lama dan object[] baru ({name, status}), buang yang kosong
  const highlights = (Array.isArray(data?.highlights) ? data?.highlights.slice(0, 3) : [])
    .map((h) => (typeof h === "string" ? h : h?.name ?? ""))
    .filter((h) => h.length > 0);

  // Tombol hanya dirender kalau ada target navigasi (url payload atau fallback program_id atau nested program.id)
  const hasTarget = Boolean(data?.url || data?.program_id || data?.program?.id);

  return (
    // Container luar: margin bawah cukup supaya card tidak ketutup input chatbot (pola sama dengan compare)
    <Box sx={{ mt: 1.5, mb: 1.5, width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: '#D0D5DD',
          borderRadius: '14px',
          p: 1.75,
          minWidth: 0,
          maxWidth: '100%',
          wordBreak: 'break-word',
          overflow: 'hidden',
        }}
      >
        {data?.title ? (
          <Typography
            sx={{
              display: 'block',
              fontWeight: 600,
              color: '#101828',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              fontSize: '0.75rem',
              mb: 0.5,
            }}
          >
            Rekomendasi
          </Typography>
        ) : null}

        {/* Nama paket utama */}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.9375rem',
            lineHeight: 1.35,
            color: '#101828',
            minWidth: 0,
            wordBreak: 'break-word',
          }}
        >
          {data?.title || "Paket"}
        </Typography>

        {/* Informasi sekunder: nama program, max 2 baris (support both old & new structure) */}
        {data?.program_title || data?.program?.title ? (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.8125rem',
              lineHeight: 1.35,
              mt: 0.25,
              minWidth: 0,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
          >
            {data?.program_title || data?.program?.title}
          </Typography>
        ) : null}

        {/* Harga: fokus visual terbesar; coret kalau ada gimmick_price yang lebih besar */}
        {hasPrice || hasGimmick ? (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mt: 1, mb: 0.5, minWidth: 0 }}>
            {hasGimmick && (
              <Typography sx={{ textDecoration: 'line-through', color: 'grey.500', fontSize: '0.8125rem' }}>
                {formatRupiah(data?.gimmick_price)}
              </Typography>
            )}
            {hasPrice && (
              <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.25, color: '#3C6F7D', minWidth: 0, wordBreak: 'break-word' }}>
                {formatRupiah(data?.price)}
              </Typography>
            )}
          </Box>
        ) : null}

        {/* Chips ringkas: durasi + mode Online/Offline */}
        {hasDuration || typeof data?.is_online === "boolean" ? (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5, minWidth: 0 }}>
            {hasDuration && (
              <Chip
                size="small"
                label={`${data?.duration_days} hari`}
                variant="outlined"
                sx={{ height: 22, fontSize: '0.75rem', color: 'text.secondary', borderColor: 'divider' }}
              />
            )}
            {typeof data?.is_online === "boolean" && (
              <Chip
                size="small"
                label={data?.is_online ? "Online" : "Offline"}
                variant="outlined"
                sx={{ height: 22, fontSize: '0.75rem', color: 'text.secondary', borderColor: 'divider' }}
              />
            )}
          </Box>
        ) : null}

        {/* Highlights: max 3, check icon + teks, teks panjang wrap + clamp visual tanpa mengubah data */}
        {highlights.length > 0 ? (
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            {highlights.map((highlight, index) => (
              <Box key={`${keyPrefix}-highlight-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
                <CheckIcon sx={{ fontSize: 16, color: '#0CA49D', flexShrink: 0, mt: 0.25 }} />
                <Typography
                  sx={{
                    minWidth: 0,
                    fontSize: '0.8125rem',
                    wordBreak: 'break-word',
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {highlight}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}

        {/* Tombol penuh: buka detail paket di tab baru (disabled saat streaming) */}
        {hasTarget && (
          <Button
            fullWidth
            variant="contained"
            disabled={disabled}
            onClick={() => {
              if (!disabled) onPriceClick?.(data);
            }}
            sx={{
              mt: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              borderRadius: 999,
              py: 0.75,
              bgcolor: '#3C6F7D',
              '&:hover': { bgcolor: '#335F6B' },
            }}
          >
            Lihat Detail Paket
          </Button>
        )}
      </Box>
    </Box>
  );
});

// BrodiCtaCard component for rendering a compact call-to-action from BRODI_CTA marker
// SATU CTA card ringkas (bukan carousel/tabel): title, subtitle, dan satu tombol "Daftar Sekarang".
const BrodiCtaCard = memo(({
  data,
  onCtaClick,
  disabled = false
}: {
  data: BrodiCtaData;
  onCtaClick?: (data: BrodiCtaData) => void;
  disabled?: boolean;
}) => {
  // Guard: tanpa data object → jangan render apapun
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;

  // Tombol hanya dirender kalau ada target navigasi (url payload atau fallback program_id)
  const hasTarget = Boolean(data?.url || data?.program_id);

  return (
    // Container luar: margin bawah cukup supaya card tidak ketutup input chatbot (pola sama dengan compare/price)
    <Box sx={{ mt: 1.5, mb: 1.5, width: '100%', minWidth: 0, maxWidth: '100%', wordBreak: 'break-word' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: '#D0D5DD',
          borderRadius: '14px',
          p: 1.75,
          minWidth: 0,
          maxWidth: '100%',
          wordBreak: 'break-word',
          overflow: 'hidden',
        }}
      >
        {data?.title ? (
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.9375rem',
              color: '#101828',
              lineHeight: 1.35,
              minWidth: 0,
              wordBreak: 'break-word',
            }}
          >
            {data.title}
          </Typography>
        ) : null}

        {data?.subtitle ? (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.8125rem',
              lineHeight: 1.4,
              mt: 0.5,
              minWidth: 0,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
          >
            {data.subtitle}
          </Typography>
        ) : null}

        {hasTarget && (
          <Button
            fullWidth
            variant="contained"
            disabled={disabled}
            onClick={() => {
              if (!disabled) onCtaClick?.(data);
            }}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
            sx={{
              mt: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              borderRadius: 999,
              py: 0.75,
              bgcolor: '#3C6F7D',
              '&:hover': { bgcolor: '#335F6B' },
            }}
          >
            Daftar Sekarang
          </Button>
        )}
      </Box>
    </Box>
  );
});

const MessageItem = memo(({ message, onButtonClick, onMateriClick, onCompareClick, onPriceClick, onCtaClick, onHelperRetry }: { message: any; onButtonClick?: (title: string) => void; onMateriClick?: (item: BrodiMateriItem) => void; onCompareClick?: (item: BrodiCompareItem, programId?: number, programUrl?: string) => void; onPriceClick?: (data: BrodiPriceData) => void; onCtaClick?: (data: BrodiCtaData) => void; onHelperRetry?: () => void }) => {
  const { mainText, thoughts } = extractThinkBlocks(message?.text ?? "");
  const segments = parseTextWithButtons(mainText);
  const [clickedMarkers, setClickedMarkers] = useState<Set<string>>(new Set());
  const [failedRetryUsed, setFailedRetryUsed] = useState(false);
  const isUser = message.sender === "user";
  const hasRichCards = !isUser && (segments.some((s) => s.type !== "text") || thoughts.length > 0);

  const handleButtonClick = (title: string, marker: string) => {
    setClickedMarkers((prev) => new Set(prev).add(marker));
    onButtonClick?.(title);
  };

  const handleHelperRetry = () => {
    if (failedRetryUsed) return;
    setFailedRetryUsed(true);
    onHelperRetry?.();
  };

  return (
    <ListItem
      sx={{
        justifyContent: isUser ? "flex-end" : "flex-start",
        alignItems: "flex-start",
        py: 0.75,
        px: 1,
      }}
    >
      {!isUser && (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            mr: 1,
            mt: 0.25,
            flexShrink: 0,
          }}
        >
          <img src="/android-chrome-192x192.png" alt="Brodi" width={20} height={20} style={{ objectFit: "contain" }} />
        </Avatar>
      )}
      <Paper
        elevation={0}
        sx={
          isUser
            ? {
                p: 1.5,
                maxWidth: "85%",
                minWidth: 0,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                borderRadius: "16px 16px 4px 16px",
                boxShadow: "none",
                transition: "all 0.2s ease",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "0.9375rem",
                lineHeight: 1.5,
                "& p": { m: 0, "& + p": { mt: 1 } },
                "& a": { color: "inherit", textDecoration: "underline" },
              }
            : {
                ...botBubbleSx,
                maxWidth: hasRichCards || message?.tools?.length ? "100%" : "88%",
              }
        }
      >
        {!isUser && <ToolCallBadges tools={message?.tools} />}
        {!isUser && <ThinkCollapsePanel thoughts={thoughts} />}
        {segments.map((segment, idx) => (
          <Fragment key={idx}>
            {segment.type === "text" && (
              <Markdown options={markdownOptions}>{segment.content}</Markdown>
            )}
            {segment.type === "failed" && (
              <BrodiParseFailedRetry
                disabled={failedRetryUsed}
                onRetry={handleHelperRetry}
              />
            )}
            {segment.type === "button" && (
              <BrodiButtonGroup
                items={segment.data.items}
                onButtonClick={(title) => handleButtonClick(title, segment.marker)}
                disabled={clickedMarkers.has(segment.marker)}
                keyPrefix={`msg-${idx}`}
              />
            )}
            {segment.type === "materi" && (
              <BrodiMateriCard
                items={segment.data.items ?? []}
                onMateriClick={onMateriClick}
                keyPrefix={`msg-materi-${idx}`}
              />
            )}
            {segment.type === "compare" && (
              <BrodiCompareCard
                items={segment.data.items ?? []}
                programId={segment.data.program_id}
                programTitle={segment.data.program_title}
                programImage={segment.data.program_image}
                programUrl={segment.data.url}
                program={segment.data.program}
                onCompareClick={onCompareClick}
                keyPrefix={`msg-compare-${idx}`}
              />
            )}
            {segment.type === "price" && (
              <BrodiPriceCard
                data={segment.data}
                onPriceClick={onPriceClick}
                keyPrefix={`msg-price-${idx}`}
              />
            )}
            {segment.type === "cta" && (
              <BrodiCtaCard
                data={segment.data}
                onCtaClick={onCtaClick}
              />
            )}
          </Fragment>
        ))}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "right",
            color: isUser ? "rgba(255,255,255,0.65)" : "text.disabled",
            mt: 0.5,
            fontSize: "0.6875rem",
            lineHeight: 1.2,
          }}
        >
          {format(new Date(message.time), "HH:mm")}
        </Typography>
      </Paper>
    </ListItem>
  );
});

export { SimpleDotsLoader, BrodiThinkingLoader, TypingBubble, MessageItem, BrodiButtonGroup, BrodiMateriCard, BrodiCompareCard, BrodiPriceCard, BrodiCtaCard, ToolCallBadges };